/**
 * WorkOrder — frontend mirror of the NestJS WorkOrder model
 * (Phase 3b backend).
 *
 * A WorkOrder is the unit of maintenance work. It targets a single
 * Equipment, lives in a vessel + company scope (denormalized for
 * fast filtering), and moves through an 8-state machine driven by
 * RBAC-gated transitions:
 *
 *   draft         created but not assigned, still editable
 *   open          assigned, waiting for technicians to start
 *   in_progress   technicians working on it
 *   pending_review finished technically, waiting for chief signature
 *   signed        chief signed with Ed25519 key, irreversible
 *   closed        archived, read-only for all except audit
 *   cancelled     cancelled before execution
 *   rejected      rejected by chief in review, back to in_progress
 *
 * The number field follows the format `WO-<shortCode>-<year>-<counter5>`
 * and is generated atomically on creation — never changes afterwards.
 */

export const WORK_ORDER_STATUSES = [
  'draft',
  'open',
  'in_progress',
  'pending_review',
  'signed',
  'closed',
  'cancelled',
  'rejected',
] as const;
export type WorkOrderStatus = (typeof WORK_ORDER_STATUSES)[number];

export const WORK_ORDER_TYPES = [
  'preventive',
  'corrective',
  'condition_based',
  'drydock',
  'improvement',
  'inspection',
] as const;
export type WorkOrderType = (typeof WORK_ORDER_TYPES)[number];

export const WORK_ORDER_PRIORITIES = [
  'low',
  'normal',
  'high',
  'critical',
] as const;
export type WorkOrderPriority = (typeof WORK_ORDER_PRIORITIES)[number];

export interface WorkOrder {
  id: string;
  number: string;
  title: string;
  description: string | null;

  type: WorkOrderType;
  status: WorkOrderStatus;
  priority: WorkOrderPriority;

  // Target (denormalized vesselId + companyId for fast filtering)
  equipmentId: string;
  vesselId: string;
  companyId: string;

  // Planning
  plannedStartAt: string | null;
  dueAt: string | null;
  estimatedHours: string | null; // Decimal serialized as string

  // Execution lifecycle timestamps
  startedAt: string | null;
  completedAt: string | null;
  signedAt: string | null;
  closedAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;

  actualHours: string | null;

  // Authorship
  createdByUserId: string;
  signedByUserId: string | null;
  lastStatusChangedBy: string | null;

  // Optional refs
  maintenancePlanId: string | null;
  drydockCampaignId: string | null;

  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

/**
 * Tailwind palette for the status badge.
 *
 * The colors trace the lifecycle:
 *   draft       slate (work-in-progress, not committed)
 *   open        sky   (assigned, awaiting start)
 *   in_progress amber (active execution)
 *   pending_review purple (waiting for signature)
 *   signed      emerald (validated, irreversible)
 *   closed      muted  (archived)
 *   cancelled   muted  (no longer relevant)
 *   rejected    red    (failed review, needs rework)
 */
export function workOrderStatusColor(status: WorkOrderStatus): string {
  switch (status) {
    case 'draft':
      return 'bg-slate-100 text-slate-700';
    case 'open':
      return 'bg-sky-100 text-sky-700';
    case 'in_progress':
      return 'bg-amber-100 text-amber-700';
    case 'pending_review':
      return 'bg-purple-100 text-purple-700';
    case 'signed':
      return 'bg-emerald-100 text-emerald-700';
    case 'closed':
      return 'bg-muted text-muted-foreground';
    case 'cancelled':
      return 'bg-muted text-muted-foreground line-through';
    case 'rejected':
      return 'bg-red-100 text-red-700';
  }
}

/** Tailwind palette for the priority badge. */
export function workOrderPriorityColor(priority: WorkOrderPriority): string {
  switch (priority) {
    case 'low':
      return 'bg-slate-100 text-slate-700';
    case 'normal':
      return 'bg-sky-100 text-sky-700';
    case 'high':
      return 'bg-orange-100 text-orange-700';
    case 'critical':
      return 'bg-red-100 text-red-700';
  }
}

/** True when the work order is in a terminal state — no further
 *  transitions are accepted, the form is read-only. */
export function isWorkOrderTerminal(status: WorkOrderStatus): boolean {
  return status === 'signed' || status === 'closed' || status === 'cancelled';
}

/** True when the work order body (title, description, planning, etc.)
 *  is editable. The backend only accepts mutations while the WO is
 *  still in draft. */
export function isWorkOrderEditable(status: WorkOrderStatus): boolean {
  return status === 'draft';
}

/**
 * WorkOrderEvent — append-only timeline entry (Phase 3c backend).
 *
 * Five subtypes share the same row, distinguished by `type`:
 *   comment        free-form note. body carries the content.
 *   status_log     auto-generated on every state transition.
 *                  payload: { from, to, reason? }
 *   measurement    technician-recorded reading.
 *                  payload: { label, value, unit }
 *   signature      Ed25519 sign event (chief).
 *                  payload: { signatureId, deviceKeyFingerprint }
 *   assignment_log assignment add/remove.
 *                  payload: { action: 'assigned'|'unassigned', userId, role }
 *
 * Status logs, measurements and signatures must NEVER be deleted
 * (PMS requirement). Comments may be soft-deleted by admins for
 * moderation.
 */
export const WORK_ORDER_EVENT_TYPES = [
  'comment',
  'status_log',
  'measurement',
  'signature',
  'assignment_log',
] as const;
export type WorkOrderEventType = (typeof WORK_ORDER_EVENT_TYPES)[number];

export interface WorkOrderEvent {
  id: string;
  workOrderId: string;
  type: WorkOrderEventType;
  authorUserId: string | null;
  body: string | null;
  payload: Record<string, unknown> | null;
  createdAt: string;
  deletedAt: string | null;
}

/**
 * WorkOrderAssignment — one technician/chief assigned to a WO.
 * Soft-removed via `unassignedAt` so audit history is preserved.
 */
export interface WorkOrderAssignment {
  id: string;
  workOrderId: string;
  userId: string;
  role: string;
  assignedAt: string;
  assignedByUserId: string;
  unassignedAt: string | null;
  unassignedByUserId: string | null;
  unassignReason: string | null;
}

/**
 * Frontend mirror of the backend state-machine transition table
 * (work-order.state-machine.ts). Used to figure out which transition
 * buttons to show on the detail page given a work order's current
 * status and the current user's roles.
 *
 * The table here is the SUBSET we actually expose to the UI — we
 * exclude `pending_review → signed` because that goes through the
 * dedicated POST /:id/sign endpoint with an Ed25519 signature, not
 * through /:id/transition. F5.4 ships the simple transitions; the
 * signing flow lands in F5.4b once the backend exposes
 * GET /:id/signature-payload.
 */
type Role =
  | 'isi_admin'
  | 'superadmin'
  | 'fleet_manager'
  | 'chief'
  | 'technician';

interface TransitionEntry {
  from: WorkOrderStatus;
  to: WorkOrderStatus;
  allowedRoles: Role[];
  /** When true, the backend additionally checks the caller is one of
   *  the active assignees. The frontend doesn't pre-check this — we
   *  let the backend reject with a translated ApiError. */
  requiresAssignment?: boolean;
}

export const WORK_ORDER_TRANSITIONS: TransitionEntry[] = [
  { from: 'draft', to: 'open', allowedRoles: ['isi_admin', 'superadmin', 'fleet_manager'] },
  { from: 'draft', to: 'cancelled', allowedRoles: ['isi_admin', 'superadmin', 'fleet_manager'] },
  { from: 'open', to: 'in_progress', allowedRoles: ['technician', 'chief', 'isi_admin', 'superadmin', 'fleet_manager'], requiresAssignment: true },
  { from: 'open', to: 'cancelled', allowedRoles: ['isi_admin', 'superadmin', 'fleet_manager'] },
  { from: 'open', to: 'draft', allowedRoles: ['isi_admin', 'superadmin', 'fleet_manager'] },
  { from: 'in_progress', to: 'pending_review', allowedRoles: ['technician', 'chief', 'isi_admin', 'superadmin'], requiresAssignment: true },
  { from: 'in_progress', to: 'cancelled', allowedRoles: ['isi_admin', 'superadmin', 'fleet_manager'] },
  { from: 'pending_review', to: 'rejected', allowedRoles: ['chief', 'isi_admin', 'superadmin'] },
  { from: 'signed', to: 'closed', allowedRoles: ['isi_admin', 'superadmin'] },
  { from: 'rejected', to: 'in_progress', allowedRoles: ['technician', 'chief', 'isi_admin', 'superadmin'], requiresAssignment: true },
  { from: 'rejected', to: 'cancelled', allowedRoles: ['isi_admin', 'superadmin', 'fleet_manager'] },
];

/** Statuses that require a `cancelReason` body field. The form/modal
 *  enforces it client-side and the backend re-validates. */
export const STATUSES_REQUIRING_REASON: WorkOrderStatus[] = ['cancelled'];

/**
 * Available transitions for the current user from a given status.
 * Filters by allowedRoles only — requiresAssignment is enforced by
 * the backend; failing it surfaces as a translated ApiError.
 */
export function availableTransitions(
  fromStatus: WorkOrderStatus,
  userRoles: string[],
): TransitionEntry[] {
  return WORK_ORDER_TRANSITIONS.filter(
    (t) =>
      t.from === fromStatus &&
      t.allowedRoles.some((r) => userRoles.includes(r)),
  );
}
